import React, { useState, useEffect } from "react";
import { GradeCriteria, Level } from "../../../../services/Dtos";
import { Flex, VStack, Text, Stack, Box, Icon, HStack } from "@chakra-ui/react";
import { colors } from "../../../../services/constants";
import { useSchoolsContext } from "../../../../Contexts/schoolContext";
import ColorPill from "../../../CustomUi/ColorPill";
import useIcons from "../../../../hooks/useIcons";
import CustomModal from "../../../CustomUi/CustomModal";
import TextInput from "../../../CustomUi/TextInput";
import SelectInput from "../../../CustomUi/SelectInput";
import CustomNumberInput from "../../../CustomUi/CustomNumberInput";
import CustomAlert from "../../../CustomUi/CustomAlert";
import { useConfirmDialog } from "../../../../hooks/useConfirmDialog";
import { useFormChanges } from "../../../../hooks/useFormChanges";
import { useFormFieldHandler } from "../../../../hooks/useFormFieldHandler";
import { useGradeCriteriaService } from "../../../../hooks/Domain/useGradeCriteriaService";
import InlineSeparator from "../../../CustomUi/InlineSeparator";
import Info from "../../../CustomUi/Info";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  gradeCriteriaToEdit: GradeCriteria | null;
  gradeLevel: Level | null;
  nextOrder: number;
}

const GradeCriteriaModal: React.FC<Props> = ({
  isOpen,
  onClose,
  gradeCriteriaToEdit,
  gradeLevel,
  nextOrder,
}) => {
  const {
    createGradeCriteria,
    updateGradeCriteria,
    deleteGradeCriteria,
    openDeleteDialog,
    setOpenDeleteDialog,
    errors,
    clearErrors,
    isLoading,
  } = useGradeCriteriaService();

  const getInitialGradeCriteriaFormState = (): GradeCriteria => ({
    id: "",
    minScore: 0,
    maxScore: 0,
    grade: "",
    description: "",
    schoolId: "",
    color: "blue",
    order: nextOrder,
    levelId: gradeLevel?.id || "",
    createdAt: "",
    updatedAt: "",
  });

  const initialGradeCriteriaFormState = getInitialGradeCriteriaFormState();

  const { levels } = useSchoolsContext();
  const icons = useIcons();
  const [selectedColor, setSelectedColor] = useState("blue");
  const [gradeCriteriaFormState, setGradeCriteriaFormState] =
    useState<GradeCriteria>(initialGradeCriteriaFormState);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Initialize confirmation dialog
  const { confirmUnsavedChanges } = useConfirmDialog();

  // Define field configurations for form validation
  const fieldConfigs = {
    grade: { type: "text" as const, required: true, maxLength: 10 },
    description: { type: "text" as const, required: true, maxLength: 100 },
    levelId: { type: "text" as const, required: true },
    maxScore: { type: "number" as const, required: true, min: 1 },
    order: { type: "number" as const, required: true, min: 1 },
  } as const;

  // Use the form changes hook
  const { hasChanges, validateField, validateAllFields, hasValidationErrors } =
    useFormChanges({
      fields: fieldConfigs,
      initialData: gradeCriteriaToEdit,
      currentData: gradeCriteriaFormState,
      isEditMode: !!gradeCriteriaToEdit,
    });

  // Use the form field handler hook
  const { handleFieldChange } = useFormFieldHandler({
    setFormState: setGradeCriteriaFormState,
    setTouchedFields,
  });

  // Real-time validation
  const validateFieldByName = (name: string, value: any): string => {
    const fieldKey = name as keyof GradeCriteria;
    return validateField(fieldKey, value);
  };

  useEffect(() => {
    if (isOpen) {
      clearErrors();
      setTouchedFields(new Set());
    }
  }, [isOpen, clearErrors]);

  useEffect(() => {
    if (gradeCriteriaToEdit) {
      setSelectedColor(gradeCriteriaToEdit.color);
      setGradeCriteriaFormState(gradeCriteriaToEdit);
    } else {
      setSelectedColor("blue");
      setGradeCriteriaFormState(getInitialGradeCriteriaFormState());
    }
    setTouchedFields(new Set());
  }, [gradeCriteriaToEdit, gradeLevel, isOpen]);

  useEffect(() => {
    setGradeCriteriaFormState((prev) => ({ ...prev, color: selectedColor }));
  }, [selectedColor]);

  const showFieldError = (fieldName: string): boolean => {
    const error = validateFieldByName(
      fieldName,
      gradeCriteriaFormState[fieldName as keyof GradeCriteria]
    );
    return !!(error && touchedFields.has(fieldName));
  };

  const getFieldError = (fieldName: string): string => {
    return validateFieldByName(
      fieldName,
      gradeCriteriaFormState[fieldName as keyof GradeCriteria]
    );
  };

  const currentValidationErrors = validateAllFields();
  const hasValidationErrorsValue = hasValidationErrors(currentValidationErrors);

  const handleSave = async () => {
    if (hasValidationErrorsValue || !hasChanges) return;

    if (gradeCriteriaToEdit) {
      const response = await updateGradeCriteria(
        gradeCriteriaToEdit.id,
        gradeCriteriaFormState
      );
      if (response.result.succeeded) {
        onClose();
      }
    } else {
      const response = await createGradeCriteria(gradeCriteriaFormState);
      if (response.result.succeeded) {
        onClose();
      }
    }
  };

  const handleDelete = async () => {
    if (!gradeCriteriaToEdit) return;
    const response = await deleteGradeCriteria(gradeCriteriaToEdit.id);
    if (response.result.succeeded) {
      onClose();
    }
  };

  const handleClose = async () => {
    if (hasChanges && gradeCriteriaToEdit) {
      const confirmed = await confirmUnsavedChanges();
      if (confirmed) {
        clearErrors();
        onClose();
      }
    } else {
      clearErrors();
      onClose();
    }
  };

  const levelOptions = levels.map((l) => ({
    label: l.levelName,
    value: l.id,
  }));

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        handleClose={handleClose}
        handleAction={handleSave}
        handleDelete={handleDelete}
        deleteMessage="All related grade criteria records and result entries will be lost permanently"
        isLoading={isLoading}
        hasChanges={hasChanges}
        hasValidationErrors={hasValidationErrorsValue}
        isDisabled={!hasChanges || hasValidationErrorsValue || isLoading}
        title={
          gradeCriteriaToEdit ? "Edit Grade Criteria" : "Add New Grade Criteria"
        }
        icon={icons.main.academic}
        mode={gradeCriteriaToEdit ? "edit" : "add"}
        actionText={
          gradeCriteriaToEdit ? "Save Changes" : "Save Grade Criteria"
        }
        openDeleteDialog={openDeleteDialog}
        setOpenDeleteDialog={setOpenDeleteDialog}
      >
        {errors.length > 0 && (
          <VStack gap={2} mb={4}>
            {errors.slice(0, 3).map((error, index) => (
              <CustomAlert key={index} message={error} />
            ))}
          </VStack>
        )}

        <VStack gap={6} align="stretch">
          {/* Grade Information Section */}
          <Box>
            <InlineSeparator label="Grade Information" />
            <VStack gap={4}>
              <TextInput
                label="Grade"
                name="grade"
                type="text"
                value={gradeCriteriaFormState.grade}
                onChange={handleFieldChange}
                placeholder="Grade (e.g., A, B, C)"
                required
                error={showFieldError("grade") ? getFieldError("grade") : ""}
                invalid={showFieldError("grade")}
              />

              <TextInput
                label="Description"
                name="description"
                type="text"
                value={gradeCriteriaFormState.description}
                onChange={handleFieldChange}
                placeholder="Grade description"
                required
                error={
                  showFieldError("description")
                    ? getFieldError("description")
                    : ""
                }
                invalid={showFieldError("description")}
              />
            </VStack>
          </Box>

          {/* Scoring Configuration Section */}
          <Box>
            <InlineSeparator label="Scoring Configuration" />
            <VStack gap={4}>
              <HStack
                gap={5}
                w={"full"}
                flexDirection={{ base: "column", md: "row" }}
                alignItems={{ base: "start", md: "center" }}
              >
                <CustomNumberInput
                  label="Minimum Score"
                  value={gradeCriteriaFormState.minScore.toString()}
                  onChange={(value) => {
                    setGradeCriteriaFormState((prev) => ({
                      ...prev,
                      minScore: parseFloat(value) || 0,
                    }));
                  }}
                  min={0}
                />

                <CustomNumberInput
                  label="Maximum Score"
                  value={gradeCriteriaFormState.maxScore.toString()}
                  onChange={(value) => {
                    setGradeCriteriaFormState((prev) => ({
                      ...prev,
                      maxScore: parseFloat(value) || 0,
                    }));
                    setTouchedFields((prev) => new Set(prev).add("maxScore"));
                  }}
                  min={0}
                  error={getFieldError("maxScore")}
                  invalid={showFieldError("maxScore")}
                />
                <SelectInput
                  label="Level"
                  name="levelId"
                  value={
                    gradeCriteriaFormState.levelId
                      ? gradeCriteriaFormState.levelId
                      : ""
                  }
                  onChange={handleFieldChange}
                  collection={levelOptions}
                  placeholder="Select Level"
                  required
                  error={
                    showFieldError("levelId") ? getFieldError("levelId") : ""
                  }
                  invalid={showFieldError("levelId")}
                />
              </HStack>
            </VStack>
          </Box>

          {/* Additional Settings Section */}
          <Box>
            <InlineSeparator label="Additional Settings" />
            <VStack gap={4}>
              <Stack my={1} w={"100%"}>
                <Text
                  as={"label"}
                  fontSize="sm"
                  fontWeight="medium"
                  display="flex"
                  alignItems="center"
                >
                  <Icon as={icons.utility.color} mr={2} />
                  Choose Grade Color
                </Text>
                <Flex
                  mt={0.5}
                  gap={2}
                  borderWidth={1}
                  p={3}
                  w={"100%"}
                  borderRadius={"md"}
                  wrap={"wrap"}
                  justifyContent={"space-between"}
                >
                  {colors.map((c, index) => (
                    <ColorPill
                      key={index}
                      color={c}
                      selectedColor={selectedColor}
                      setSelectedColor={setSelectedColor}
                    />
                  ))}
                </Flex>
              </Stack>
              <CustomNumberInput
                label="Order"
                value={gradeCriteriaFormState.order.toString()}
                onChange={(value) => {
                  setGradeCriteriaFormState((prev) => ({
                    ...prev,
                    order: parseFloat(value) || 0,
                  }));
                  setTouchedFields((prev) => new Set(prev).add("order"));
                }}
                min={1}
                error={getFieldError("order")}
                invalid={showFieldError("order")}
                info={
                  <Info content="The order of the grade criteria in a list of grade criteria. This helps with arranging the grade criteria the way you want." />
                }
              />
            </VStack>
          </Box>
        </VStack>
      </CustomModal>
    </>
  );
};

export default GradeCriteriaModal;
